var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useTranslation } from 'react-i18next';
import { TableCell, TableRow } from '../../common';
import { ExpandableRowLabel } from '../../common/tableHierarchy';
import { QueueType } from '../../multiskill/types';
import { useResourcingContext } from '../../report/hooks';
import { DisplayMode } from '../../report/resourcing';
import { useDisplayModeParams } from '../../report/selectors';
import { SEATS_REQUIRED_SECTION_ID } from '../../seatCapacity';
import { FirstCell, STICKIES } from '../annualTable';
import { AnnualSectionTable } from '../common/annualSectionTable';
import { MultigeoCountryRow, MultigeoLobRow } from '../common/multigeoQueuesByLob';
import { MultiskillGroupRows } from '../common/multiskillGroupRow';
import { getCountryExpansionKey, useControlledExpansion, useMultiskillGroupExpansionKeys, useMultiskillGroupNames, useQueueExpansionKeys, useSelectQueuesByLobPerScenario } from '../common/selectors';
import { getChunkBaselineValue, getExpandChildren, getHighlightBorder, getScenarioCellColor } from '../common/utils';
import { CALCULATE_DIFF, DECIMAL_VALUE, HIERARCHY_LEVEL, NEST_LEVEL, PERCENTAGES, TOTAL_ROW_COLOR, getLobExpansionKey, getPrefixedExpansionKey } from '../constants';
import { useSummaryByDisplayMode, useSummaryChunkSize } from '../selectors';
export var getSummaryCountries = function (data) {
  var countriesByName = new Map();
  data.forEach(function (scenario) {
    var _a;
    (_a = scenario.countries) === null || _a === void 0 ? void 0 : _a.forEach(function (country) {
      var existingCountry = countriesByName.get(country.countryName);
      if (!existingCountry) {
        countriesByName.set(country.countryName, __assign(__assign({}, country), {
          locations: country.locations.map(function (location) {
            var _a;
            return __assign(__assign({}, location), {
              queues: __spreadArray([], (_a = location.queues) !== null && _a !== void 0 ? _a : [], true)
            });
          })
        }));
        return;
      }
      var existingLocationsByName = new Map(existingCountry.locations.map(function (location) {
        return [location.locationName, location];
      }));
      country.locations.forEach(function (location) {
        var _a, _b, _c;
        var existingLocation = existingLocationsByName.get(location.locationName);
        if (!existingLocation) {
          var newLocation = __assign(__assign({}, location), {
            queues: __spreadArray([], (_a = location.queues) !== null && _a !== void 0 ? _a : [], true)
          });
          existingCountry.locations.push(newLocation);
          existingLocationsByName.set(location.locationName, newLocation);
          return;
        }
        // Union queues across scenarios, same as locations are unioned across
        // countries above -- a queue present in one scenario but not another must
        // still appear in the tree so its per-scenario value lookup can find it.
        var existingQueueNames = new Set(((_b = existingLocation.queues) !== null && _b !== void 0 ? _b : []).map(function (locationQueue) {
          return locationQueue.queueName;
        }));
        ((_c = location.queues) !== null && _c !== void 0 ? _c : []).forEach(function (locationQueue) {
          var _a;
          if (!existingQueueNames.has(locationQueue.queueName)) {
            existingLocation.queues = __spreadArray(__spreadArray([], (_a = existingLocation.queues) !== null && _a !== void 0 ? _a : [], true), [__assign({}, locationQueue)], false);
            existingQueueNames.add(locationQueue.queueName);
          }
        });
      });
    });
  });
  return Array.from(countriesByName.values());
};
export var SummaryCountryLocationFieldSection = function (_a) {
  var fieldKey = _a.fieldKey,
    expandedProp = _a.expandedProp,
    onToggle = _a.onToggle,
    expandedItems = _a.expandedItems,
    onToggleItem = _a.onToggleItem,
    fallbackRows = _a.fallbackRows,
    useLobHierarchy = _a.useLobHierarchy;
  var t = useTranslation('report', {
    keyPrefix: 'annual.reportRows'
  }).t;
  var data = useSummaryByDisplayMode();
  var chunkSize = useSummaryChunkSize();
  var isPercentages = useResourcingContext().isPercentages;
  var displayMode = useDisplayModeParams()[0];
  var multiskillGroupNames = useMultiskillGroupNames(data !== null && data !== void 0 ? data : []);
  var _b = useControlledExpansion(expandedProp, onToggle),
    expanded = _b.expanded,
    handleToggle = _b.handleToggle;
  if (!data) return null;
  var totalPerScenario = data.map(function (s) {
    var _a;
    return (_a = s[fieldKey]) !== null && _a !== void 0 ? _a : null;
  });
  var countries = getSummaryCountries(data);
  var getCountryScenarioValues = function (country) {
    return data.map(function (scenario) {
      var _a, _b, _c;
      return (_c = (_b = (_a = scenario.countries) === null || _a === void 0 ? void 0 : _a.find(function (item) {
        return item.countryName === country.countryName;
      })) === null || _b === void 0 ? void 0 : _b[fieldKey]) !== null && _c !== void 0 ? _c : null;
    });
  };
  var getLocationScenarioValues = function (country, location) {
    return data.map(function (scenario) {
      var _a, _b, _c, _d;
      return (_d = (_c = (_b = (_a = scenario.countries) === null || _a === void 0 ? void 0 : _a.find(function (item) {
        return item.countryName === country.countryName;
      })) === null || _b === void 0 ? void 0 : _b.locations.find(function (item) {
        return item.locationName === location.locationName;
      })) === null || _c === void 0 ? void 0 : _c[fieldKey]) !== null && _d !== void 0 ? _d : null;
    });
  };
  return _jsxs(_Fragment, {
    children: [_jsxs(TableRow, {
      children: [_jsx(FirstCell, __assign({
        "aria-label": NEST_LEVEL[HIERARCHY_LEVEL.total],
        stickySizes: STICKIES,
        bold: true,
        color: TOTAL_ROW_COLOR
      }, {
        children: _jsx(ExpandableRowLabel, __assign({
          expanded: expanded,
          onToggle: handleToggle,
          bold: true
        }, {
          children: t(fieldKey)
        }))
      })), totalPerScenario.map(function (value, index, arr) {
        var _a, _b;
        var baseValue = (_a = getChunkBaselineValue(totalPerScenario, index, chunkSize)) !== null && _a !== void 0 ? _a : null;
        return _jsx(TableCell, {
          colSpan: 2,
          value: value,
          decimal: (_b = DECIMAL_VALUE[fieldKey]) !== null && _b !== void 0 ? _b : 0,
          percentage: PERCENTAGES.includes(fieldKey),
          bold: true,
          color: getScenarioCellColor(data[index]),
          highlightBorder: getHighlightBorder(index, arr.length, chunkSize),
          expandchildren: displayMode !== DisplayMode.All && CALCULATE_DIFF.includes(fieldKey) ? getExpandChildren(displayMode, baseValue, value, isPercentages) : undefined,
          useColorValue: true
        }, "total-".concat(fieldKey, "-").concat(index));
      })]
    }), expanded && (useLobHierarchy || countries.length === 0) && fallbackRows, expanded && !useLobHierarchy && countries.map(function (country) {
      var countryExpansionKey = getCountryExpansionKey(fieldKey, country.countryName);
      return _jsx(MultigeoCountryRow, {
        country: country,
        scenarioValues: getCountryScenarioValues(country),
        scenarioLocationValues: country.locations.map(function (location) {
          return getLocationScenarioValues(country, location);
        }),
        data: data,
        chunkSize: chunkSize,
        keyItem: fieldKey,
        expandedProp: expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems[countryExpansionKey],
        onToggle: function () {
          return onToggleItem === null || onToggleItem === void 0 ? void 0 : onToggleItem(countryExpansionKey);
        },
        hideLocationQueueRows: true,
        nestLevelOffset: 2
      }, "".concat(fieldKey, "-").concat(country.countryName));
    }), expanded && _jsx(MultiskillGroupRows, {
      groupNames: multiskillGroupNames,
      data: data,
      chunkSize: chunkSize,
      valueKey: fieldKey,
      expandedItems: expandedItems,
      onToggleItem: onToggleItem
    })]
  });
};
export var SummaryCountryLocationMetricTable = function (_a) {
  var _b;
  var title = _a.title,
    unit = _a.unit,
    metricKey = _a.metricKey,
    sectionId = _a.sectionId;
  var data = useSummaryByDisplayMode();
  var lobsPerScenario = useSelectQueuesByLobPerScenario();
  var countries = data ? getSummaryCountries(data) : [];
  var isMultiskillPlan = (_b = data === null || data === void 0 ? void 0 : data.some(function (scenario) {
    return scenario.dataType === QueueType.Multiskill;
  })) !== null && _b !== void 0 ? _b : false;
  var useLobHierarchy = lobsPerScenario.length > 0;
  var countryExpansionKeys = useLobHierarchy ? [] : countries.map(function (country) {
    return getCountryExpansionKey(metricKey, country.countryName);
  });
  var lobExpansionKeys = lobsPerScenario.map(function (lobEntry) {
    return getPrefixedExpansionKey(metricKey, getLobExpansionKey(lobEntry.lobName));
  });
  var queueExpansionKeys = useQueueExpansionKeys(lobsPerScenario, metricKey);
  var multiskillExpansionItems = useMultiskillGroupExpansionKeys(data !== null && data !== void 0 ? data : []);
  var Section = function (_a) {
    var expandedItems = _a.expandedItems,
      onToggleItem = _a.onToggleItem;
    var chunkSize = useSummaryChunkSize();
    var fallbackRows = data ? lobsPerScenario.map(function (lobEntry) {
      return _jsx(MultigeoLobRow, {
        lob: lobEntry.template,
        perScenarioLob: lobEntry.perScenarioLob,
        data: data,
        chunkSize: chunkSize,
        keyItem: metricKey,
        hideConstraintHeader: true,
        hideMultiskillQueues: isMultiskillPlan,
        expandedItems: expandedItems,
        onToggleItem: onToggleItem,
        expansionKeyPrefix: metricKey
      }, "".concat(metricKey, "-").concat(lobEntry.lobName));
    }) : null;
    return _jsx(SummaryCountryLocationFieldSection, {
      fieldKey: metricKey,
      expandedProp: expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems[metricKey],
      onToggle: function () {
        return onToggleItem === null || onToggleItem === void 0 ? void 0 : onToggleItem(metricKey);
      },
      expandedItems: expandedItems,
      onToggleItem: onToggleItem,
      useLobHierarchy: useLobHierarchy,
      fallbackRows: fallbackRows
    });
  };
  return _jsx(AnnualSectionTable, {
    title: title,
    unit: unit,
    expansionItems: __spreadArray(__spreadArray(__spreadArray(__spreadArray([metricKey], countryExpansionKeys, true), lobExpansionKeys, true), queueExpansionKeys, true), multiskillExpansionItems, true),
    tableBodyComponent: Section,
    getNestedExpansionItems: function (itemName) {
      return itemName === metricKey ? __spreadArray(__spreadArray(__spreadArray(__spreadArray([], countryExpansionKeys, true), lobExpansionKeys, true), queueExpansionKeys, true), multiskillExpansionItems, true) : [];
    },
    sectionId: sectionId
  });
};
export var SeatsRequiredTable = function () {
  var t = useTranslation('report', {
    keyPrefix: 'annual'
  }).t;
  return _jsx(SummaryCountryLocationMetricTable, {
    title: t('seatsRequired'),
    unit: t('numberOfSeats'),
    metricKey: "seatsRequired",
    sectionId: SEATS_REQUIRED_SECTION_ID
  });
};